import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';
import {
  AuthApi,
  UsersApi,
  LoginDto,
  ClientLoginDto,
  VerifyCodeDto,
  LoginResponseDto,
  LoginResponseDtoApiResponse,
  BooleanApiResponse,
  UserRole,
} from '@ata-digital/client-tax-system-api-client';
import { createApiConfig, apiAxiosInstance } from '@/lib/api/config';
import { tokenManager, clientTokenManager, isClientContext, getActiveTokenManager } from '@/lib/auth/tokenManager';
import { createLogger } from '@/utils/logger';

const log = createLogger('useAuth');

// Query Keys for consistent caching
export const authKeys = {
  all: ['auth'] as const,
  user: () => [...authKeys.all, 'user'] as const,
  session: () => [...authKeys.all, 'session'] as const,
} as const;

export interface AuthState {
  token: string | null;
  refreshToken: string | null;
  userType: 'staff' | 'client' | null;
  isAuthenticated: boolean;
  user: {
    id?: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    fullName?: string;
    phoneNumber?: string;
    lastLoginAt?: string;
    role?: UserRole;
  } | null;
}

/**
 * Hook to get current authentication state from TanStack Query cache.
 *
 * Uses route-aware token manager selection:
 * - Client routes (/, /documents, etc.) → clientTokenManager (sessionStorage)
 * - Preparer routes (/preparer/*) → tokenManager (localStorage)
 *
 * This separation ensures that preparer and client sessions don't interfere
 * with each other when both are open in different browser tabs.
 *
 * Also fetches user profile if authenticated but user data is missing (fallback for SSO).
 */
export function useAuthState() {
  return useQuery({
    queryKey: authKeys.session(),
    queryFn: async (): Promise<AuthState> => {
      log.debug('queryFn called');

      // Get the appropriate token manager based on current route context
      const activeManager = getActiveTokenManager();
      const inClientContext = isClientContext();

      log.debug('Auth context:', { inClientContext });

      // Get auth state from the active TokenManager
      const token = activeManager.getStoredToken();
      const refreshToken = activeManager.getStoredRefreshToken();
      const userType = activeManager.getUserType();

      log.debug('Current state:', {
        hasToken: !!token,
        hasRefreshToken: !!refreshToken,
        userType,
        storage: inClientContext ? 'sessionStorage' : 'localStorage',
      });

      // Check if we have a valid token (not just stored token)
      // ⚠️ IMPORTANT: Don't catch errors here - let them bubble to TanStack Query
      // This allows tokenManager's error handling to dispatch events before query error state
      const validToken = await activeManager.getValidToken();
      const isAuthenticated = !!validToken;

      log.debug('Token validation result:', { isAuthenticated });

      // Get stored user data from the active manager
      let user = activeManager.getStoredUser();

      // Fallback: If authenticated but no user data, fetch from profile endpoint
      // This handles cases where SSO login didn't successfully fetch user data
      if (isAuthenticated && validToken && !user) {
        log.debug('User data missing, attempting to fetch profile');
        try {
          const usersApi = new UsersApi(createApiConfig(validToken), undefined, apiAxiosInstance);
          const response = await usersApi.apiUsersProfileGet();

          const userDto = response.data?.data;
          if (userDto) {
            // TODO: Consider creating a shared helper function to extract UserInfo from UserDto
            // This pattern is repeated in useAuth.ts and useSsoAuth.ts
            user = {
              id: userDto.id,
              email: userDto.email || undefined,
              firstName: userDto.firstName || undefined,
              lastName: userDto.lastName || undefined,
              fullName: userDto.fullName || undefined,
              phoneNumber: userDto.phoneNumber || undefined,
              lastLoginAt: userDto.lastLoginAt || undefined,
              role: userDto.role,
            };
            log.info('User profile fetched successfully (fallback)', { userId: user.id });

            // Store user data for future requests using the active manager
            const authMethod = activeManager.getAuthMethod();
            activeManager.setTokens({
              authToken: validToken,
              refreshToken: refreshToken || '',
              userType: userType || 'staff',
              authMethod: authMethod || 'password',
              user,
            });
          }
        } catch (error) {
          // Log but don't fail - auth state should still work without user data
          log.warn('Failed to fetch user profile (fallback):', error);
        }
      }

      return {
        token: activeManager.getStoredToken(),
        refreshToken: activeManager.getStoredRefreshToken(),
        userType: activeManager.getUserType(),
        isAuthenticated,
        user,
      };
    },
    retry: false, // Never retry auth state checks - fail fast on 401
    staleTime: 5 * 60 * 1000, // Revalidate every 5 minutes
    gcTime: Infinity, // Keep auth state in cache
    refetchOnWindowFocus: true, // Revalidate when window gains focus
  });
}

/**
 * Hook for staff/preparer login
 */
export function useStaffLogin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (credentials: LoginDto) => {
      // Use the API client for authentication
      const authApi = new AuthApi(createApiConfig(), undefined, apiAxiosInstance);

      const response = await authApi.apiAuthLoginPost({
        loginDto: credentials,
      });

      // Handle the actual API response structure
      // The backend wraps the response in a success/data structure
      const responseData = response.data as LoginResponseDtoApiResponse;

      // Check if the response has the nested structure
      if (responseData?.success && responseData?.data) {
        const authData = responseData.data;

        if (authData.token && authData.refreshToken) {
          return {
            token: authData.token,
            refreshToken: authData.refreshToken,
            isFirstLogin: authData.isFirstLogin || false,
            user: authData.user
              ? {
                  id: authData.user.id,
                  email: authData.user.email || undefined,
                  firstName: authData.user.firstName || undefined,
                  lastName: authData.user.lastName || undefined,
                  fullName: authData.user.fullName || undefined,
                  phoneNumber: authData.user.phoneNumber || undefined,
                  lastLoginAt: authData.user.lastLoginAt || undefined,
                  role: authData.user.role,
                }
              : undefined,
          };
        }
      }

      // Fallback: check if token and refreshToken are at root level
      if (
        response.data &&
        typeof response.data === 'object' &&
        'token' in response.data &&
        'refreshToken' in response.data
      ) {
        const authData = response.data as LoginResponseDto;
        return {
          token: authData.token!,
          refreshToken: authData.refreshToken!,
          isFirstLogin: authData.isFirstLogin || false,
          user: authData.user
            ? {
                id: authData.user.id,
                email: authData.user.email || undefined,
                firstName: authData.user.firstName || undefined,
                lastName: authData.user.lastName || undefined,
                fullName: authData.user.fullName || undefined,
                phoneNumber: authData.user.phoneNumber || undefined,
                lastLoginAt: authData.user.lastLoginAt || undefined,
                role: authData.user.role,
              }
            : undefined,
        };
      }

      // If response doesn't have the expected structure, throw an error
      throw new Error('Invalid login response format');
    },
    onSuccess: (data) => {
      if (!data.user) {
        log.warn('Login response missing user data: But the login was successful.');
      }

      // Store isFirstLogin flag in sessionStorage for contact info check
      if (data.isFirstLogin) {
        sessionStorage.setItem('isFirstLogin', 'true');
      } else {
        sessionStorage.removeItem('isFirstLogin');
      }

      // Store tokens and user info using TokenManager
      tokenManager.setTokens({
        authToken: data.token,
        refreshToken: data.refreshToken,
        userType: 'staff',
        user: data.user,
      });

      // Update auth state in cache
      queryClient.setQueryData(authKeys.session(), {
        token: data.token,
        refreshToken: data.refreshToken,
        userType: 'staff',
        isAuthenticated: true,
        user: data.user || null,
      });

      // Invalidate user data to refresh
      queryClient.invalidateQueries({ queryKey: authKeys.user() });
    },
  });
}

/**
 * Hook for client login - Step 1: Send verification code
 */
export function useClientLoginInitiate() {
  // Use no authentication for client login initiation since user isn't authenticated yet
  const authApi = new AuthApi(createApiConfig(''), undefined, apiAxiosInstance);

  return useMutation({
    mutationFn: async (credentials: ClientLoginDto) => {
      const response = await authApi.apiAuthClientLoginPost({ clientLoginDto: credentials });

      // Extract the clientId from the response
      const responseData = response?.data as Record<string, unknown>;
      const actualData = responseData?.data as Record<string, unknown>;
      const clientId = actualData?.clientId;

      if (!clientId) {
        throw new Error('ClientId not received from server');
      }

      return {
        success: true,
        message: actualData?.message || 'Verification code sent',
        clientId: clientId,
        expiresAt: actualData?.expiresAt,
      };
    },
    onError: (error) => {
      log.error('Client login initiation failed:', error);
    },
  });
}

/**
 * Hook to resend verification code during client login
 */
export function useResendVerificationCode() {
  const authApi = new AuthApi(createApiConfig(''), undefined, apiAxiosInstance);

  return useMutation({
    mutationFn: async (emailOrPhone: string) => {
      const response = await authApi.apiAuthSendVerificationCodePost({
        sendVerificationCodeDto: {
          // NOTE: Backend API design quirk - field is named 'email' but accepts phone numbers
          // Other auth DTOs use 'emailOrPhone' (ClientLoginDto, VerifyCodeDto), but
          // SendVerificationCodeDto uses 'email' field for both email and phone.
          // The 'method' field determines how the backend interprets the value.
          email: emailOrPhone,
          method: emailOrPhone.includes('@') ? 'email' : 'sms',
        },
      });

      // Extract response from nested structure: response.data.data contains the boolean result
      const responseData = response?.data as BooleanApiResponse;
      const success = responseData?.data === true;

      if (!success) {
        throw new Error('Failed to resend verification code');
      }

      return { success: true, message: 'Verification code resent successfully' };
    },
    onError: (error) => {
      log.error('Resend verification code failed:', error);
    },
  });
}

/**
 * Hook for client login - Step 2: Verify SMS code
 */
// Prevent duplicate mutation calls with a ref-based lock
let isVerifyingMutation = false;
let verificationPromise: Promise<{
  token: string;
  refreshToken: string;
  isFirstLogin: boolean;
  user?: {
    id?: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    fullName?: string;
    phoneNumber?: string;
    lastLoginAt?: string;
    role?: UserRole;
  };
}> | null = null;

export function useClientLoginVerify() {
  const queryClient = useQueryClient();
  // Use no authentication for verify-code endpoint since user isn't authenticated yet
  const authApi = new AuthApi(createApiConfig(''), undefined, apiAxiosInstance);

  return useMutation({
    mutationFn: async (params: {
      verificationData: Omit<VerifyCodeDto, 'emailOrPhone'>;
      emailOrPhone: string;
    }): Promise<{
      token: string;
      refreshToken: string;
      isFirstLogin: boolean;
      user?: {
        id?: string;
        email?: string;
        firstName?: string;
        lastName?: string;
        fullName?: string;
        phoneNumber?: string;
        lastLoginAt?: string;
        role?: UserRole;
      };
    }> => {
      // If already verifying, return the existing promise
      if (isVerifyingMutation && verificationPromise) {
        return verificationPromise;
      }

      // Set lock and create verification promise
      isVerifyingMutation = true;

      const verifyCodeDto: VerifyCodeDto = {
        ...params.verificationData,
        emailOrPhone: params.emailOrPhone,
      };

      verificationPromise = (async (): Promise<{
        token: string;
        refreshToken: string;
        isFirstLogin: boolean;
        user?: {
          id?: string;
          email?: string;
          firstName?: string;
          lastName?: string;
          fullName?: string;
          phoneNumber?: string;
          lastLoginAt?: string;
          role?: UserRole;
        };
      }> => {
        try {
          const response = await authApi.apiAuthVerifyCodePost({ verifyCodeDto });

          const typedResponse = response as AxiosResponse<LoginResponseDtoApiResponse>;

          // Handle the nested API response structure: response.data.data contains the actual LoginResponseDto
          if (
            typedResponse.data &&
            typeof typedResponse.data === 'object' &&
            'data' in typedResponse.data &&
            typedResponse.data.data &&
            typeof typedResponse.data.data === 'object' &&
            'token' in typedResponse.data.data &&
            'refreshToken' in typedResponse.data.data
          ) {
            const authData = typedResponse.data.data as LoginResponseDto;
            return {
              token: authData.token!,
              refreshToken: authData.refreshToken!,
              isFirstLogin: authData.isFirstLogin || false,
              user: authData.user
                ? {
                    id: authData.user.id,
                    email: authData.user.email || undefined,
                    firstName: authData.user.firstName || undefined,
                    lastName: authData.user.lastName || undefined,
                    fullName: authData.user.fullName || undefined,
                    phoneNumber: authData.user.phoneNumber || undefined,
                    lastLoginAt: authData.user.lastLoginAt || undefined,
                    role: authData.user.role,
                  }
                : undefined,
            };
          }

          // If response doesn't have the expected structure, throw an error
          throw new Error('Invalid verification response format');
        } catch (error) {
          // Reset lock on error to allow retry
          isVerifyingMutation = false;
          verificationPromise = null;
          throw error;
        }
      })();

      return await verificationPromise;
    },
    onSuccess: (data: {
      token: string;
      refreshToken: string;
      isFirstLogin: boolean;
      user?: {
        id?: string;
        email?: string;
        firstName?: string;
        lastName?: string;
        fullName?: string;
        phoneNumber?: string;
        lastLoginAt?: string;
        role?: UserRole;
      };
    }) => {
      // Store isFirstLogin flag in sessionStorage for contact info check
      if (data.isFirstLogin) {
        sessionStorage.setItem('isFirstLogin', 'true');
      } else {
        sessionStorage.removeItem('isFirstLogin');
      }

      // Store tokens and user info using clientTokenManager (sessionStorage)
      // This ensures client auth is isolated per browser tab and doesn't
      // interfere with preparer sessions in localStorage
      clientTokenManager.setTokens({
        authToken: data.token,
        refreshToken: data.refreshToken,
        userType: 'client',
        user: data.user,
      });

      // Update auth state in cache
      queryClient.setQueryData(authKeys.session(), {
        token: data.token,
        refreshToken: data.refreshToken,
        userType: 'client',
        isAuthenticated: true,
        user: data.user || null,
      });

      // Invalidate user data to refresh
      queryClient.invalidateQueries({ queryKey: authKeys.user() });
    },
    onError: () => {
      // Error handled by UI components
    },
  });
}

/**
 * Hook for token refresh.
 *
 * Uses route-aware token manager to refresh tokens in the correct storage.
 */
export function useTokenRefresh() {
  const queryClient = useQueryClient();
  const { data: authState } = useAuthState();

  const authApi = new AuthApi(createApiConfig(authState?.token || undefined), undefined, apiAxiosInstance);

  return useMutation({
    mutationFn: async () => {
      if (!authState?.refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await authApi.apiAuthRefreshTokenPost({
        refreshTokenDto: { refreshToken: authState.refreshToken },
      });

      // Handle the actual API response structure
      if (
        response.data &&
        typeof response.data === 'object' &&
        'token' in response.data &&
        'refreshToken' in response.data
      ) {
        const authData = response.data as LoginResponseDto;
        return {
          token: authData.token!,
          refreshToken: authData.refreshToken!,
          user: authData.user
            ? {
                id: authData.user.id,
                email: authData.user.email || undefined,
                firstName: authData.user.firstName || undefined,
                lastName: authData.user.lastName || undefined,
                fullName: authData.user.fullName || undefined,
                phoneNumber: authData.user.phoneNumber || undefined,
                lastLoginAt: authData.user.lastLoginAt || undefined,
              }
            : undefined,
        };
      }

      // If response doesn't have the expected structure, throw an error
      throw new Error('Invalid refresh response format');
    },
    onSuccess: (data) => {
      // Use route-aware token manager for storage
      const activeManager = getActiveTokenManager();
      const userType = activeManager.getUserType();
      const currentUser = activeManager.getStoredUser();
      activeManager.setTokens({
        authToken: data.token,
        refreshToken: data.refreshToken,
        userType: userType || 'staff', // Default to staff if no user type
        user: data.user || currentUser || undefined, // Use new user data if available, otherwise keep current
      });

      // Update auth state in cache
      queryClient.setQueryData(authKeys.session(), {
        ...authState,
        token: data.token,
        refreshToken: data.refreshToken,
        user: data.user || currentUser || null,
      });
    },
    onError: (error) => {
      log.error('Token refresh failed:', error);
      // Clear auth state on refresh failure using route-aware TokenManager
      const activeManager = getActiveTokenManager();
      activeManager.clearTokens();

      // Clear ALL query cache to remove stale authenticated data
      queryClient.clear();

      // Update auth state to logged out
      queryClient.setQueryData(authKeys.session(), {
        token: null,
        refreshToken: null,
        userType: null,
        isAuthenticated: false,
        user: null,
      });

      // Dispatch event to trigger navigation to login
      window.dispatchEvent(new CustomEvent('auth:token-expired'));
    },
  });
}

/**
 * Hook for logout.
 *
 * Handles both regular logout and SSO logout:
 * - For SSO users: Gets Azure AD sign-out URL and redirects for full sign-out
 * - For password users: Just clears local auth state
 *
 * Uses route-aware token manager to clear the correct storage:
 * - Client routes → clears sessionStorage
 * - Preparer routes → clears localStorage
 */
export function useLogout() {
  const queryClient = useQueryClient();
  const { data: authState } = useAuthState();

  const authApi = new AuthApi(createApiConfig(authState?.token || undefined), undefined, apiAxiosInstance);

  return useMutation({
    mutationFn: async (): Promise<{ ssoLogoutUrl: string | null }> => {
      const activeManager = getActiveTokenManager();
      const authMethod = activeManager.getAuthMethod();
      let ssoLogoutUrl: string | null = null;

      log.debug('Logout initiated', {
        authMethod,
        hasToken: !!authState?.token,
        isClientContext: isClientContext(),
      });

      // Call backend logout endpoint
      if (authState?.token) {
        try {
          await authApi.apiAuthLogoutPost({});
        } catch (error) {
          // Log but don't fail - we still want to clear local state
          log.warn('Backend logout failed:', error);
        }
      }

      // For SSO users (preparers only), get the Azure AD sign-out URL
      if (authMethod === 'sso') {
        try {
          log.debug('SSO user detected, fetching Azure AD logout URL');
          const response = await authApi.apiAuthSsoLogoutGet();
          // StringApiResponse structure: { success?, data?, error?, meta? }
          ssoLogoutUrl = response.data?.data || null;
          log.debug('Azure AD logout URL received:', { hasUrl: !!ssoLogoutUrl });
        } catch (error) {
          log.warn('Failed to get SSO logout URL:', error);
          // Don't throw - fall back to regular logout
        }
      }

      return { ssoLogoutUrl };
    },
    onSettled: (_data) => {
      // Always clear auth state, even if logout API call fails
      // Use route-aware token manager to clear the correct storage
      const activeManager = getActiveTokenManager();
      activeManager.clearTokens();

      // Clear all cached data
      queryClient.clear();

      // Set auth state to logged out
      queryClient.setQueryData(authKeys.session(), {
        token: null,
        refreshToken: null,
        userType: null,
        isAuthenticated: false,
        user: null,
      });
    },
    onSuccess: (data) => {
      // If we have an SSO logout URL, redirect to Azure AD for full sign-out
      if (data.ssoLogoutUrl) {
        log.info('Redirecting to Azure AD for SSO sign-out');
        window.location.href = data.ssoLogoutUrl;
      }
      // For non-SSO users, the calling component will handle navigation
    },
  });
}
/**
 * Convenience hook that returns the auth state data directly.
 * Use this when you need quick access to user/token data.
 * Use useAuthState() when you need the full query result (loading states, etc).
 */
export function useAuth() {
  const { data } = useAuthState();
  return (
    data || {
      token: null,
      refreshToken: null,
      userType: null,
      isAuthenticated: false,
      user: null,
    }
  );
}
